// ============================================================================
// use-route-planner — ALL Route Optimizer state (2026-07-31 run).
//
// The architecture fence from the map run: <LoadedMap> stays read-only and
// route-agnostic. Every route semantic — selection set, ordered plan, mode
// flag, next-stop cursor — lives HERE, in a pure reducer plus a thin hook.
// FacilityMap composes this and passes presentational props down.
//
// D-2: no persistence — the plan lives until the user leaves the view.
// Manual reorder always wins: reorder/remove recompute leg miles for the
// manual order via legsForOrder(); the optimizer is only re-run when the
// user explicitly taps Optimize again.
// ============================================================================

import { useCallback, useReducer } from 'react';
import {
  MAX_ROUTE_STOPS,
  orderRoute,
  TooManyStopsError,
  type OrderedRoute,
  type RouteStop,
} from './route-order';
import { legsForOrder, type LatLng, type OrderLeg } from './route-links';

export interface PlannerStop extends RouteStop {
  name: string;
  /**
   * CW-C Phase 1 (2026-08-04): carried so the NEXT-STOP handoff (a single
   * destination) has an address to fall back to. Multi-stop route MATH still
   * uses lat/lng from RouteStop only; that is the hard rule and it does not
   * change.
   */
  address: string | null;
  /**
   * CW-C Phase 4 (2026-08-05): whether this stop's coordinate has ROOFTOP
   * provenance and so becomes the next-stop routing destination instead of the
   * address. Derive with isCoordTrusted(geocode_precision) at the call site;
   * false/absent means address-first. Affects the SINGLE-destination handoff
   * only — never the route math.
   */
  coordTrusted?: boolean;
}

export interface PlannerState {
  active: boolean;
  /** selection, insertion-ordered */
  stops: PlannerStop[];
  /** true right after a tap/add-all hit the 30-stop cap */
  capHint: boolean;
  /** current visit order (optimized, then manually adjusted); null = no plan */
  orderedIds: string[] | null;
  legs: OrderLeg[] | null;
  totalMiles: number | null;
  /** start point the current plan was computed with (for recomputes) */
  start: LatLng | null;
  /** next-stop cursor for >10-stop handoff */
  nextIndex: number;
  /** unreachable by construction (cap), but handled: optimizer error text */
  planError: string | null;
}

export const initialPlannerState: PlannerState = {
  active: false,
  stops: [],
  capHint: false,
  orderedIds: null,
  legs: null,
  totalMiles: null,
  start: null,
  nextIndex: 0,
  planError: null,
};

export type PlannerAction =
  | { type: 'enter' }
  | { type: 'exit' }
  | { type: 'toggle'; stop: PlannerStop }
  | { type: 'addAll'; stops: PlannerStop[] }
  | { type: 'planned'; result: OrderedRoute; start: LatLng | null }
  | { type: 'planFailed'; message: string }
  | { type: 'reorder'; from: number; to: number }
  | { type: 'remove'; id: string }
  | { type: 'advance' };

function recompute(
  state: PlannerState,
  stops: PlannerStop[],
  orderedIds: string[] | null,
): PlannerState {
  if (!orderedIds) {
    return { ...state, stops, orderedIds: null, legs: null, totalMiles: null };
  }
  const kept = orderedIds.filter((id) => stops.some((s) => s.id === id));
  if (kept.length === 0) {
    return {
      ...state,
      stops,
      orderedIds: null,
      legs: null,
      totalMiles: null,
      nextIndex: 0,
    };
  }
  const { legs, totalMiles } = legsForOrder(stops, kept, state.start);
  return {
    ...state,
    stops,
    orderedIds: kept,
    legs,
    totalMiles,
    nextIndex: Math.min(state.nextIndex, kept.length - 1),
  };
}

export function plannerReducer(
  state: PlannerState,
  action: PlannerAction,
): PlannerState {
  switch (action.type) {
    case 'enter':
      return { ...initialPlannerState, active: true };
    case 'exit':
      return initialPlannerState;

    case 'toggle': {
      const has = state.stops.some((s) => s.id === action.stop.id);
      if (has) {
        const stops = state.stops.filter((s) => s.id !== action.stop.id);
        const ordered = state.orderedIds
          ? state.orderedIds.filter((id) => id !== action.stop.id)
          : null;
        return recompute({ ...state, capHint: false }, stops, ordered);
      }
      if (state.stops.length >= MAX_ROUTE_STOPS) {
        return { ...state, capHint: true };
      }
      const stops = [...state.stops, action.stop];
      // adding after a plan exists: append to the end of the manual order
      const ordered = state.orderedIds
        ? [...state.orderedIds, action.stop.id]
        : null;
      return recompute({ ...state, capHint: false }, stops, ordered);
    }

    case 'addAll': {
      const have = new Set(state.stops.map((s) => s.id));
      const incoming = action.stops.filter((s) => !have.has(s.id));
      const room = MAX_ROUTE_STOPS - state.stops.length;
      const accepted = incoming.slice(0, Math.max(room, 0));
      const stops = [...state.stops, ...accepted];
      const ordered = state.orderedIds
        ? [...state.orderedIds, ...accepted.map((s) => s.id)]
        : null;
      return recompute(
        { ...state, capHint: accepted.length < incoming.length },
        stops,
        ordered,
      );
    }

    case 'planned': {
      const withStart = { ...state, start: action.start, planError: null };
      const { legs, totalMiles } = legsForOrder(
        state.stops,
        action.result.order,
        action.start,
      );
      return {
        ...withStart,
        orderedIds: [...action.result.order],
        legs,
        totalMiles,
        nextIndex: 0,
      };
    }

    case 'planFailed':
      return { ...state, planError: action.message };

    case 'reorder': {
      if (!state.orderedIds) return state;
      const { from, to } = action;
      if (
        from === to ||
        from < 0 ||
        to < 0 ||
        from >= state.orderedIds.length ||
        to >= state.orderedIds.length
      ) {
        return state;
      }
      const ordered = [...state.orderedIds];
      const [moved] = ordered.splice(from, 1);
      ordered.splice(to, 0, moved);
      const { legs, totalMiles } = legsForOrder(state.stops, ordered, state.start);
      return { ...state, orderedIds: ordered, legs, totalMiles };
    }

    case 'remove': {
      const stops = state.stops.filter((s) => s.id !== action.id);
      const ordered = state.orderedIds
        ? state.orderedIds.filter((id) => id !== action.id)
        : null;
      return recompute({ ...state, capHint: false }, stops, ordered);
    }

    case 'advance': {
      const max = (state.orderedIds?.length ?? 1) - 1;
      return { ...state, nextIndex: Math.min(state.nextIndex + 1, max) };
    }

    default:
      return state;
  }
}

// --- hook -------------------------------------------------------------------

/** Geolocation as a promise; resolves null on denial/timeout/unavailable —
 *  the prompt's fallback is "no start → algorithm picks" (D-delta-1),
 *  and a denied permission must never error-loop. */
function getPositionOrNull(): Promise<LatLng | null> {
  return new Promise((resolve) => {
    if (!('geolocation' in navigator)) {
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { maximumAge: 300_000, timeout: 8_000 },
    );
  });
}

export interface RoutePlanner {
  state: PlannerState;
  enter: () => void;
  exit: () => void;
  toggleStop: (stop: PlannerStop) => void;
  addAll: (stops: PlannerStop[]) => void;
  optimize: () => Promise<void>;
  reorder: (from: number, to: number) => void;
  removeStop: (id: string) => void;
  advance: () => void;
}

export function useRoutePlanner(): RoutePlanner {
  const [state, dispatch] = useReducer(plannerReducer, initialPlannerState);

  const optimize = useCallback(async () => {
    const stops = state.stops;
    if (stops.length === 0) return;
    const start = await getPositionOrNull();
    try {
      const result = orderRoute(stops, { start: start ?? undefined });
      dispatch({ type: 'planned', result, start });
    } catch (e) {
      // TooManyStopsError is unreachable by construction (selection caps at
      // 30) but handled per the prompt: honest message, no crash.
      const message =
        e instanceof TooManyStopsError
          ? e.message
          : 'Could not order the route — try again.';
      dispatch({ type: 'planFailed', message });
    }
  }, [state.stops]);

  return {
    state,
    enter: useCallback(() => dispatch({ type: 'enter' }), []),
    exit: useCallback(() => dispatch({ type: 'exit' }), []),
    toggleStop: useCallback(
      (stop: PlannerStop) => dispatch({ type: 'toggle', stop }),
      [],
    ),
    addAll: useCallback(
      (stops: PlannerStop[]) => dispatch({ type: 'addAll', stops }),
      [],
    ),
    optimize,
    reorder: useCallback(
      (from: number, to: number) => dispatch({ type: 'reorder', from, to }),
      [],
    ),
    removeStop: useCallback((id: string) => dispatch({ type: 'remove', id }), []),
    advance: useCallback(() => dispatch({ type: 'advance' }), []),
  };
}
